import { CollectionRepository, Id } from '../abstraction';
import { StorageResourceLocation } from '../../data/specialized/storage/storage.location';
import { ReadonlyStorageCollectionRepository } from './readonly-storage-collection.repository';
import { toIdString } from '../functions/to-id-string';
import { Dictionary } from '../../types/dictionary';
import { StorageDataAccess } from '../../data-access/abstraction/storage.data-access';

export abstract class StorageCollectionRepository<TItem, TParams> extends ReadonlyStorageCollectionRepository<TItem, TParams> implements CollectionRepository<TItem, TItem[], TItem, TParams, TParams> {

  protected constructor(dataAccess: StorageDataAccess,
              key: string) {
    super(dataAccess, key);
  }

  async create(value: TItem, params: TParams): Promise<TItem> {
    const location: StorageResourceLocation = {
      key: this.getKey(params)
    };
    const id = this.getId(value);
    const idString = toIdString(id);
    const existing = this.dataAccess.get<Dictionary<TItem>>(location) ?? {};
    if(existing[idString])
      throw new Error(`[StorageCollectionDataService]create: Object with id '${id}' already exists in collection '${this.key}'`);
    this.dataAccess.set<Dictionary<TItem>>(location, { ...existing, [idString]: value });
    return value;
  }

  async update(id: Id, value: TItem, params: TParams): Promise<TItem> {
    const location: StorageResourceLocation = {
      key: this.getKey(params)
    };
    const idString = toIdString(id);

    const existing =  this.dataAccess.get<Dictionary<TItem>>(location) ?? {};
    if(!existing[idString])
      throw new Error(`[StorageCollectionDataService]update: Object with id '${id}' does not exist in collection '${this.key}'`);
    this.dataAccess.set(location, { ...existing, [idString]: value });
    return value;
  }

  async patch(id: Id, changes: Partial<TItem>, params: TParams): Promise<TItem> {
    const location: StorageResourceLocation = {
      key: this.getKey(params)
    };
    const idString = toIdString(id);
    const existing =  this.dataAccess.get<Dictionary<TItem>>(location) ?? {};
    if(!existing[idString])
      throw new Error(`[StorageCollectionDataService]patch: Object with id '${id}' does not exist in collection '${this.key}'`);

    const newValue = {...existing[idString], ...changes};
    this.dataAccess.set(location, { ...existing, [idString]: newValue});
    return newValue;
  }

  async delete(id: Id, params: TParams): Promise<TItem> {
    const location: StorageResourceLocation = {
      key: this.getKey(params)
    };
    const idString = toIdString(id);
    const existing =  this.dataAccess.get<Dictionary<TItem>>(location) ?? {};
    if(!existing[idString])
      throw new Error(`[StorageCollectionDataService]patch: Object with id '${id}' does not exist in collection '${this.key}'`);
    const dict = { ...existing };
    delete dict[idString];
    this.dataAccess.set(location, dict);
    return existing[idString];
  }

  abstract getId(value: TItem): Id

}
